import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {combineLatest, Subject} from "rxjs";
import {BuilderSectionsInteractionService} from "../../../../builder-sections-interaction.service";
import {BuilderV2Service} from "../../../../builder-v2.service";
import {filter, first, takeUntil, tap} from "rxjs/operators";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {User} from "@sentry/browser";
import {BuilderStepsEnum, BuilderTourEnum} from "../../../../builder-tour.enum";
import {BuilderHistoryService} from "../../../../builder-history.service";
import {ActivatedRoute, Router} from "@angular/router";
import {IDsDropdownOption} from "../../../../../shared/components/ds-dropdown/ds-dropdown-option";
import {Utils} from "../../../../../shared/utils/utils";
import {TextUtils} from "../../../../../shared/utils/text-utils";
import {Config} from "../../../../../config/config";
import {TriggersService} from "../../../../services/triggers.service";
import {CookieService} from "ngx-cookie";
import {VibeCodingFlowService} from "../../../../../shared/services/vibe-coding-flow.service";
import {CommonBuilderDataService} from "../../../../services/common-builder-data.service";


@Component({
    selector: 'db-prefilled-company-info',
    templateUrl: './prefilled-company-info.component.html',
    styleUrls: ['./prefilled-company-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PrefilledCompanyInfoComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('companyName') companyNameEl: ElementRef;
  @ViewChild('companyDescription') companyDescriptionEl: ElementRef;

  @Input() data: any;
  @Input() user: User;
  @Input() uniqueId: string;
  @Input() isEcommerce = false;
  @Input() domainId: number;

  @Output() onMobileGenerate = new EventEmitter<boolean>();

  isMobile = false;
  public websiteTypes: IDsDropdownOption[] = [
    {value: 'basic', label: TextUtils.getText('text_informational') + ' ' + TextUtils.getText('text_website')},
    {value: 'ecommerce', label: 'Online store'},
  ];

  websiteType: string = '';
  outlineFormGroup: FormGroup;
  isBusinessType = false;
  showInitialTitle = false;
  showInitialDescription = false;
  showEnhancedDescription = false;
  showAnalyzingTitle = false;
  showOutlineTitle = false;
  showEnhancedTitle = false;
  enhancedDescription: string | null = null;
  companyName: string;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private fb: FormBuilder,
    public builderInteractionsService: BuilderSectionsInteractionService,
    private _builderHistoryService: BuilderHistoryService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _builderV2Service: BuilderV2Service,
    private elRef: ElementRef,
    private renderer: Renderer2,
    private _builderTriggerService: TriggersService,
    private _cdr: ChangeDetectorRef,
    private _cookieService: CookieService,
    private _vibeCodingFlowService: VibeCodingFlowService,
    private _commonDataService: CommonBuilderDataService,
    private _builderSectionsInteractionService: BuilderSectionsInteractionService,
  ) {
    this.isEcommerce = this.builderInteractionsService.getWebsiteTypeMobile() === 'ecommerce' || this.builderInteractionsService.websiteType.value === 'ecommerce';
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 991;
    this.isMobile && this.checkMobileData();
    this.uniqueId = this._builderHistoryService.getUniqueId();
    this.isMobile && this._setProductId();
    this._getCompanyInfo();
    if (this.builderInteractionsService.prefilledDescription.value) {
        this.startDescriptionTransition();
    }
  }

  private _getCompanyInfo(): void {
    this._builderSectionsInteractionService.companyInfo
        .pipe(
            filter(companyInfo => !!companyInfo),
            takeUntil(this._destroyStream$),
            first()
        )
        .subscribe(companyInfo => {
          this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
        });
  }

  private _setProductId() {
    const vibeCodingVersion = localStorage.getItem('builderType');
    const productId = localStorage.getItem('product_id') || vibeCodingVersion ? Config.vibeCodingPluginId : Config.builder2HostedPluginId ;
    (!this.user || (this.user && !!localStorage.getItem('dbDemoCampaign'))) && localStorage.setItem('product_id', productId.toString());
  }

  ngAfterViewInit() {
    const inputs = this.elRef.nativeElement.querySelectorAll('input, textarea');

    inputs.forEach((input: HTMLElement) => {
      // Disable zoom on the first focus
      this.renderer.listen(input, 'focus', () => this.disableZoom());
      this.renderer.listen(input, 'touchstart', () => this.disableZoom());
    });
  }

  disableZoom() {
    const viewport = document.querySelector("meta[name=viewport]");
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );
    }
  }

  checkMobileData() {
    const state = this._builderHistoryService.getFormData();

    if (state) {
      this.data = {...state.oldFormData, ...state.formData};
      this.outlineFormGroup = state.formData;
      this.builderInteractionsService.loading.next(false);
      this.builderInteractionsService.disableNextButton.next(false);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  ecommerceNext() {
    this._onboardingDomainName();
  }

  onGenerate() {
    const vibeCodingFlow = this._vibeCodingFlowService.isCombinedOutline();
    this._commonDataService.combinedOutline = vibeCodingFlow;

    this.builderInteractionsService.loading.next(true);

    if (this.outlineFormGroup?.invalid) {
      this.builderInteractionsService.loading.next(false);
      this.outlineFormGroup.markAllAsTouched();
      return;
    }
    const formValue = this._getFormValue();
    this._onboardingDomainName();
    this.builderInteractionsService.siteInfo.next({
      ...formValue,
      domain_name: ''
    });
    if (this.isMobile) {
      const data = {formData: this.outlineFormGroup?.value, siteInfo: this._getFormValue()};
      this._builderHistoryService.saveFormData(data);
      const state = this._builderHistoryService.getFormData();
      if (this.isEcommerce) {
        !this.user ? this._router.navigate(['../fill-products'], {relativeTo: this._route})
          : this._builderHistoryService.builderStep.next(BuilderStepsEnum.PRODUCT_TYPE);
      } else {
        this.user ? this.onMobileGenerate.emit() : this._router.navigate(['../mobile-sign-up'], {relativeTo: this._route});
      }
      this.user && this._builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_FINALIZE);
    } else {
      this._builderHistoryService.tourStep.next(BuilderTourEnum.START);
      this._builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);
      this._builderTriggerService.getPageMetas.next();
      combineLatest([
        this._builderV2Service.aiGenerateOutline(formValue, this.domainId, this.uniqueId)
          .pipe(tap(value => {
            if (value) {
            }
          })),
        this._builderV2Service.aiGenerateStyles({...formValue}, this.domainId, this.uniqueId)
      ]).subscribe(([outline, styles]) => {
        if ((styles && !styles.success) || (outline && !outline.success)) {
          NotifierComponent.notify(new Notification(outline?.message || styles?.message, NotificationType.DANGER, true));
        }
      });
    }
  }

  private _getFormValue() {
    return {
      'business_description': this.companyDescriptionEl.nativeElement.value as string,
      'business_type': this.outlineFormGroup?.value?.business_type as string,
      'business_name': this.companyNameEl.nativeElement.value as string
    };
  }

  private _onboardingDomainName(): void {
    const domainInfo = {
      "want_domain": 0,
      "domain_name": ""
    };
    const _domainInfo = JSON.stringify(domainInfo);
    localStorage.setItem('dbOnboardingDomainInfo', _domainInfo);
    if (this.isMobile) {
      const mobileDomainInfo = {mobileDomainInfo: _domainInfo};
      this._builderHistoryService.saveFormData(mobileDomainInfo);
    }
  }

  startDescriptionTransition(): void {
    this.showAnalyzingTitle = false;
    this.showInitialTitle = false;
    this.showInitialDescription = false;
    this.showEnhancedTitle = false;
    this.showEnhancedDescription = false;
    this.showOutlineTitle = false;
    this._cdr.detectChanges();

    // Step 1: Show initial description immediately
    this.showInitialDescription = true;
    this._cdr.detectChanges();

    // Step 2: Show analyzing title after 1.5s
    setTimeout(() => {
      this.showAnalyzingTitle = true;
      this._cdr.detectChanges();

      if (this.builderInteractionsService.enhancedDescription$.value) {
        this.startEnhancedAnimation();
      } else {
        const checkEnhancedDescription = setInterval(() => {
          if (this.builderInteractionsService.enhancedDescription$.value) {
            clearInterval(checkEnhancedDescription);
            this.startEnhancedAnimation();
          }
        }, 200); // Check every 200ms
      }

    }, 1500); // **1.5s delay before showing analyzing title**
  }

  private startEnhancedAnimation(): void {

    // Step 4: Hide initial title, show enhanced title **2.5s later**
    setTimeout(() => {
      this.showAnalyzingTitle = false;
      this.showEnhancedTitle = true;
      this._cdr.detectChanges();

      // Step 5: Show enhanced description **1s after enhanced title appears**
      setTimeout(() => {
        this.showInitialDescription = false;
        this.showEnhancedDescription = true;
        this._cdr.detectChanges();

        setTimeout(() => {
          this.showEnhancedTitle = false;
          this.showOutlineTitle = true;
          this._cdr.detectChanges();

          setTimeout(() => {
            this.builderInteractionsService.enhancedDescriptionIsDone.next(true);
            this._cdr.detectChanges();
          }, 500);
        }, 4000);
      }, 1500);
    }, 2000);
  }



}
